import { EntityFile, MARK_FAILED, UPDATE } from './EntityFile.js';
import { FileStorage, } from './FileStorage.js';
/**
 * Default: if file was deleted > 3 days ago
 */
function defaultCanCleanup(fileData) {
    return (fileData.deletedAt !== null &&
        fileData.deletedAt < Date.now() - 1000 * 60 * 24 * 3);
}
export class FileManager {
    constructor({ db, sync, context, meta, config = {}, }) {
        this.files = new Map();
        this.add = async (fileInput) => {
            const file = fileInput;
            file.remote = false;
            // immediately cache the file
            if (!this.files.has(file.id)) {
                const entityFile = new EntityFile(file.id);
                entityFile[UPDATE](file);
                this.files.set(file.id, entityFile);
            }
            else {
                this.files.get(file.id)[UPDATE](file);
            }
            // write to local storage and send to sync immediately
            await this.storage.addFile(file);
            // send to sync
            if (file.file) {
                await this.uploadFile(file, 1);
            }
        };
        this.uploadFile = async (file, retries = 0) => {
            const result = await this.sync.uploadFile(file);
            if (result.success) {
                await this.storage.markUploaded(file.id);
            }
            else {
                if (result.retry && retries < 5) {
                    this.context.log('error', 'Error uploading file, retrying...');
                    // schedule a retry
                    setTimeout(this.uploadFile, 1000, file, retries + 1);
                }
                else {
                    this.context.log('error', 'Failed to upload file. Not retrying until next sync.');
                }
            }
        };
        /**
         * Immediately returns an EntityFile to use, then either loads
         * the file from cache, local database, or the server.
         */
        this.get = (id, options) => {
            if (this.files.has(id)) {
                return this.files.get(id);
            }
            const file = new EntityFile(id, options);
            this.files.set(id, file);
            this.load(file);
            return file;
        };
        this.load = async (file, retries = 0) => {
            if (retries > 5) {
                this.context.log('error', 'Failed to load file after 5 retries');
                file[MARK_FAILED]();
                return;
            }
            const fileData = await this.storage.getFile(file.id);
            if (fileData) {
                file[UPDATE](fileData);
            }
            else {
                // maybe we don't have it yet, it might be on the server still.
                try {
                    const result = await this.sync.getFile(file.id);
                    if (result.success) {
                        file[UPDATE](result.data);
                        await this.storage.addFile(result.data, {
                            downloadRemote: file.downloadRemote,
                        });
                    }
                    else {
                        this.context.log('error', 'Failed to load file', result);
                        file[MARK_FAILED]();
                        if (result.retry) {
                            // schedule a retry
                            setTimeout(this.load, 1000, file, retries + 1);
                        }
                    }
                }
                catch (err) {
                    this.context.log('error', 'Failed to load file', err);
                    file[MARK_FAILED]();
                    // schedule a retry
                    setTimeout(this.load, 1000, file, retries + 1);
                }
            }
        };
        this.listUnsynced = async () => {
            return this.storage.listUnsynced();
        };
        this.exportAll = async (downloadRemote = false) => {
            const storedFiles = await this.storage.getAll();
            if (downloadRemote) {
                for (const storedFile of storedFiles) {
                    // if it doesn't have a buffer, we need to read
                    // one from the server
                    if (!storedFile.file && storedFile.url) {
                        try {
                            const blob = await fetch(storedFile.url, {
                                method: 'GET',
                                credentials: 'include',
                            }).then((r) => r.blob());
                            storedFile.file = blob;
                        }
                        catch (err) {
                            this.context.log('error', "Failed to download file to cache it locally. The file will still be available using its URL. Check the file server's CORS configuration.", err);
                        }
                    }
                }
            }
            return storedFiles;
        };
        this.importAll = async (files) => {
            await Promise.all(files.map((file) => this.add(file)));
        };
        this.onOnlineChange = async (online) => {
            // if online, try to upload any unsynced files
            if (online) {
                const unsynced = await this.listUnsynced();
                await Promise.all(unsynced.map(this.uploadFile));
            }
        };
        this.tryCleanupDeletedFiles = async () => {
            let count = 0;
            let skipCount = 0;
            await this.storage.iterateOverPendingDelete((fileData, store) => {
                if (this.config.canCleanupDeletedFile(fileData)) {
                    count++;
                    store.delete(fileData.id);
                }
                else {
                    skipCount++;
                }
            });
            this.context.log('info', `Cleaned up ${count} files, skipped ${skipCount} files`);
        };
        this.handleFileRefsDeleted = async (fileRefs) => {
            const tx = this.storage.createTransaction(['files'], { mode: 'readwrite' });
            await Promise.all(fileRefs.map(async (fileRef) => {
                try {
                    await this.storage.markPendingDelete(fileRef.id, { transaction: tx });
                }
                catch (err) {
                    this.context.log('error', 'Failed to mark file for deletion', err);
                }
            }));
            this.context.log('info', `Marked ${fileRefs.length} files as pending delete`);
        };
        this.close = () => {
            this.storage.dispose();
        };
        this.storage = new FileStorage(db);
        this.sync = sync;
        this.context = context;
        this.meta = meta;
        this.config = Object.assign({ canCleanupDeletedFile: defaultCanCleanup }, config);
        this.sync.subscribe('onlineChange', this.onOnlineChange);
        this.meta.subscribe('filesDeleted', this.handleFileRefsDeleted);
        // check on startup to see if files can be cleaned up
        this.tryCleanupDeletedFiles();
    }
}
//# sourceMappingURL=FileManager.js.map